// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The English bundle, and the shape every other bundle has to match (01 §6, 08 §3).
//
// **This file defines the keys.** `MessageKey` is `keyof typeof EN`, so a bundle that is missing a
// key does not compile and a key nobody uses is visible as a key nobody uses. That is the whole
// reason the English is written out as an object literal rather than loaded: a translation table
// checked at runtime is a translation table that fails on somebody's screen, in the language they
// cannot read, at the moment they were trying to pay.
//
// **Placeholders are `{name}` and are substituted by `format`, never by concatenation at the call
// site.** French does not put its words in English order — "save 8%" is "économisez 8 %", and the
// space before the percent sign is required — so a sentence assembled from fragments in a component
// is a sentence that can only ever be right in one language.
//
// **Nothing here is a machine's own content.** Titles, callouts, rule names and display text live
// inside machine definitions, which authors write; localising those is not possible and is not
// attempted. What this covers is the product's own chrome: what things cost, what a tier buys, why
// a refusal happened, and the controls that change those.

export const EN = {
  // -------------------------------------------------------------------------
  // Plans and money
  // -------------------------------------------------------------------------
  'plan.monthly': 'Monthly',
  'plan.annual': 'Annual',
  'plan.per.month': 'month',
  'plan.per.year': 'year',
  'plan.note.cancel-any-time': 'Cancel any time.',
  'plan.note.one-month-free': 'One month free.',
  'plan.a-month': '{amount} a month',
  'plan.save': 'save {percent}%',
  'plan.subscribe-title': 'Subscribe {plan} at {amount}',

  // -------------------------------------------------------------------------
  // The paywall
  // -------------------------------------------------------------------------
  'paywall.heading': 'Pro',
  'paywall.lead': 'That is part of Pro. Here is everything else it adds.',
  'paywall.free-heading': 'A free account, first',
  'paywall.free-lead':
    'These cost nothing and do not need a subscription. You do not need an account to play at all.',
  'paywall.subscribe': 'Subscribe',
  'paywall.opening': 'Opening checkout…',
  'paywall.signin': 'Sign in to subscribe.',
  'paywall.checkout-failed': 'Checkout could not be started.',

  // -------------------------------------------------------------------------
  // The subscription panel
  // -------------------------------------------------------------------------
  'subscription.heading': 'Pro',
  'subscription.checking': 'Checking your subscription…',
  'subscription.through': 'Subscribed through',
  'subscription.renews': 'Renews',
  'subscription.never-expires': 'Never — this does not expire',
  'subscription.manage': 'Manage subscription',
  'subscription.manage-title': 'Open Stripe to change or cancel your subscription',
  'subscription.opening': 'Opening…',
  'subscription.portal-failed': 'Billing management could not be opened.',
  'subscription.elsewhere-meta':
    'This subscription is billed by Meta. Change or cancel it in your Meta account settings.',
  'subscription.elsewhere-other': 'This subscription is not billed through the web.',

  // -------------------------------------------------------------------------
  // Providers
  // -------------------------------------------------------------------------
  'provider.stripe': 'the web',
  'provider.meta': 'Meta',
  'provider.support': 'a support grant',
  'provider.system': 'this account',
  'provider.double-paying':
    'You are subscribed on {providers}. Pro is not doubled and neither subscription is cancelled automatically — you can end either one where you started it.',
  'provider.and': 'and',

  // -------------------------------------------------------------------------
  // What each tier buys — 01 §6's own labels
  // -------------------------------------------------------------------------
  'capability.play-web': 'NEON NOVA in the browser, desktop or phone',
  'capability.local-scores': 'Local high scores in that browser',
  'capability.cloud-scores': 'Cloud score sync, game history, replays',
  'capability.leaderboards': 'Global and friends leaderboards',
  'capability.hotseat': '1–4 player alternating-ball games, same device',
  'capability.online-games': 'Online alternating-ball games with friends',
  'capability.tournaments': 'Weekly tournaments',
  'capability.community-machines': 'Play machines published by other players',
  'capability.builder-private': 'Machine Builder — design and play privately',
  'capability.builder-publish': 'Machine Builder — publish publicly',
  'capability.builder-api': 'Machine Builder — build with your own AI, and the Builder API',
  'capability.headset': 'Headset VR and MR (WebXR and native)',
  'capability.arcade': 'The shared virtual arcade, avatars and spatial voice',
  'capability.full-roster': 'Full machine roster beyond NEON NOVA',

  // -------------------------------------------------------------------------
  // Pro refusals, by capability — the client's own copy of `server/ts/proGate.ts`
  // -------------------------------------------------------------------------
  'pro.community-machines': 'Machines made by other players are part of Pro. NEON NOVA is free, always.',
  'pro.builder-private': 'The Machine Builder is part of Pro. NEON NOVA is free to play without it.',
  'pro.builder-publish':
    'Publishing a machine to the arcade is part of Pro. Your drafts stay yours either way.',
  'pro.builder-api': 'Building machines from your own tools and AI is part of Pro.',
  'pro.headset': 'Playing in a headset is part of Pro.',
  'pro.arcade': 'The shared arcade, avatars and voice are part of Pro.',
  'pro.full-roster': 'This machine is part of Pro. NEON NOVA is free, always.',
  'pro.default': 'That is part of Pro.',

  // -------------------------------------------------------------------------
  // Error codes — the contract's closed vocabulary
  // -------------------------------------------------------------------------
  'error.bad_request': 'Something about that request was not right.',
  'error.unauthenticated': 'Sign in to do that.',
  'error.forbidden': 'That is not yours to do.',
  'error.not_found': 'That is not here.',
  'error.conflict': 'Something changed while you were working. Have another look.',
  'error.idempotency_mismatch': 'That looked like a repeat of something different. Try again.',
  'error.rate_limited': 'Too much at once. Give it a moment.',
  'error.payload_too_large': 'That is too big to send.',
  'error.entitlement_required': 'That is part of Pro.',
  'error.moderation_blocked': 'That cannot be published as it is.',
  'error.validation_failed': 'That machine cannot be saved yet.',
  'error.unsupported_core_version': 'This game was played on a version the server cannot re-run.',
  'error.client_update_required': 'This app is out of date. Reload to get the current one.',
  'error.service_unavailable': 'That is not working right now. Try again shortly.',
  'error.internal_error': 'Something went wrong at our end.',
  'error.offline': 'You appear to be offline.',
  'error.unknown': 'That did not work.',

  // -------------------------------------------------------------------------
  // The arcade roster
  // -------------------------------------------------------------------------
  'arcade.loading': 'Loading the rest of the arcade…',
  'arcade.era.em': 'Electromechanical, 1970s',
  'arcade.era.ss': 'Solid state, 1980s',
  'arcade.era.dmd': 'Dot matrix, 1990s',
  'arcade.era.lcd': 'Modern',
  'arcade.era.unknown': 'Not downloaded yet',
  'arcade.badge.pro': 'Pro',
  'arcade.badge.free-this-week': 'Free this week',
  'arcade.badge.free': 'Free',
  // **The build card's own badge, and the reason it is not `arcade.badge.pro`.** Every other card
  // in the grid leads somewhere a locked viewer cannot go, and "Pro" is the honest word for those.
  // This one leads into a workspace that opens for anybody — 01 §6's *"the Builder is a sandbox,
  // not a locked door"* — so the roster's badge was advertising a padlock on the one door that has
  // none, and a badge is what a reader takes in before any title attribute.
  'arcade.badge.try': 'Free to try',
  'arcade.locked-title': '{title} is part of Pro',
  // **The step in front of somebody who has never signed in.** They cannot subscribe either, so
  // "part of Pro" is true and useless; the sentence says both, in the order they happen.
  'arcade.signin-title': 'Sign in to play {title} — it is part of Pro',
  // Shown on the account page when a locked tile sent them there, so the click informs rather than
  // merely navigates. `{title}` is the machine they tapped.
  'account.want.full-roster': 'Sign in to play {title}. The rest of the roster is part of Pro. NEON NOVA and the free machine of the week need no account at all.',
  'account.want.full-roster-plain': 'Sign in to play the rest of the roster. It is part of Pro. NEON NOVA and the free machine of the week need no account at all.',
  'arcade.play-title': 'Play {title}',
  'arcade.devices': '{count} devices',
  'arcade.build.name': 'Build a new machine',
  'arcade.build.meta': 'Design your own playfield, rules and sound',
  'arcade.build.title': 'Open the Machine Builder',
  'arcade.build.title-pro':
    'Open the Machine Builder — designing and test-playing are free, keeping a machine is part of Pro',
  'arcade.guest-lede':
    'One machine changes every week and is free to everyone. The rest are part of Pro.',

  // -------------------------------------------------------------------------
  // Settings
  // -------------------------------------------------------------------------
  'settings.language': 'Language',
  'settings.language-hint':
    'Changes the words in the app. A machine’s own callouts and display text are part of the machine and do not change.',
  'settings.language.en': 'English',
  'settings.language.fr': 'Français',
  'settings.language-title': 'Read this app in {language}',
  'settings.currency': 'Currency',
  'settings.currency-hint':
    'Which currency prices are shown and charged in. Each is a price of its own, not a conversion.',
  'settings.currency.USD': 'US dollars',
  'settings.currency.CAD': 'Canadian dollars',
  'settings.currency-title': 'Show and charge prices in {currency}',
  'settings.rating': 'What the arcade shows',
  'settings.rating-hint':
    'Authors rate their own machines. This app is rated 13+, so everything is shown unless you ask for less.',
  'settings.rating.everyone': 'Everyone only',
  'settings.rating.teen': 'Everything',
  'settings.rating-title': 'Show {rating} in the arcade',
  'settings.rating-note':
    'A machine with no rating of its own is treated as if it were rated for teens.',

  // -------------------------------------------------------------------------
  // The Builder sandbox
  // -------------------------------------------------------------------------
  'builder.sandbox-heading': 'You are trying the Machine Builder',
  'builder.sandbox-lead':
    'Build anything you like and test-play it right here. Keeping a machine — saving it, playing it on another device, publishing it — is part of Pro.',
  // **Rewritten when it stopped being true (M9-32).** The draft was React state and died on a
  // reload, so this sentence was correct for four milestones and then quietly became a lie that
  // told an author their work was gone. What a subscription buys is not the machine surviving a
  // reload — it survives one now — it is the machine leaving this browser.
  'builder.sandbox-warning':
    'Your machine is kept in this browser and nowhere else. Clearing this browser’s data, or opening the site on another device, loses it.',
  'builder.save-needs-pro': 'Keep this machine — Pro',

  // The way in, named once and used by every page that offers it: the pricing page, the settings
  // page, both dead ends of `/play`, the 404 and the end of a game. One door, one name — a product
  // that calls the same destination three things teaches nobody where it is.
  'builder.open': 'Build a machine',
  'builder.open-title': 'Open the Machine Builder — designing and test-playing are free',

  // -------------------------------------------------------------------------
  // The shell, settings, the error boundary and the roster
  // -------------------------------------------------------------------------
  'shell.skip': 'Skip to the machine',
  'settings.account': 'Account',
  'settings.account-hint':
    'Optional. An account puts your scores on a leaderboard and lets you add friends — the machine plays exactly the same without one.',
  'settings.account-link': 'Account and leaderboards',
  // The Builder, beside the account and for the same reason that one is here: `NAV_TABS` marks
  // Build non-essential and `tabsFor` drops it at 400 CSS pixels, so on a phone in portrait the
  // roster's build card was the only way in and it sits below the whole roster. "You" is one of
  // the three tabs a narrow bar keeps.
  'settings.builder': 'Machine Builder',
  'settings.builder-hint':
    'Design a playfield, rules, art and sound of your own. Designing and test-playing are free; keeping a machine is part of Pro.',
  // Sound (07 §7). Ahead of appearance, because a machine that is too loud is a problem the
  // player is having right now and a theme is a preference they are expressing.
  'settings.audio': 'Sound',
  'settings.audio-hint':
    'Every sound this machine makes is generated as it plays — there are no audio files. Turning a part of it down takes effect the next time a machine is opened.',
  'settings.audio.master': 'The machine',
  'settings.audio.master-hint':
    'The same control as the speaker button on the machine itself. Turning it back on returns everything to where you left it below. Other players’ voices in the arcade have their own control and are not affected.',
  'settings.audio.on': 'On',
  'settings.audio.off': 'Off',
  'settings.audio-title.on': 'Let the machine make sound',
  'settings.audio-title.off': 'Silence the machine completely',
  'settings.audio.volume': 'Overall volume',
  'settings.audio.volume-hint':
    'How loud everything is, before the four controls below. Turning this down keeps the balance you have set between them.',
  'settings.audio-volume-title': 'Set the overall volume to {level}',
  'settings.audio.music': 'Music',
  'settings.audio.music-hint':
    'The cabinet’s soundtrack. It already steps back on its own while the machine is speaking.',
  'settings.audio.effects': 'Sound effects',
  'settings.audio.effects-hint':
    'Flippers, bumpers, targets, the ball rolling on the playfield, and the score chimes over the top of them.',
  'settings.audio.captions': 'Callout captions',
  'settings.audio.captions-hint':
    'Show what the machine says as text. Independent of the volume and of the mute, so a callout '
    + 'you cannot hear is still one you can read.',
  'settings.audio-title.captions-on': 'Show callout captions',
  'settings.audio-title.captions-off': 'Hide callout captions',
  'settings.audio.speech': 'Callouts',
  'settings.audio.speech-hint':
    'The machine telling you things — a saved ball, a lit jackpot. Captions still appear when this is off.',
  'settings.audio.ambience': 'Arcade ambience',
  'settings.audio.ambience-hint':
    'The room around the machine: other cabinets, a little reverb. Mostly heard in a headset.',
  'settings.audio.level.full': 'Full',
  'settings.audio.level.low': 'Quieter',
  'settings.audio.level.off': 'Off',
  'settings.audio-level-title': 'Set {group} to {level}',
  'settings.appearance': 'Appearance',
  'settings.appearance-hint':
    'Matching your device follows it when it changes — including at sunset, if your phone does that.',
  'settings.theme.system': 'Match my device',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',
  'settings.theme-title': 'Use the {theme} appearance',
  'settings.flashing': 'Flashing',
  'settings.flashing-hint':
    'A pinball machine blinks a great deal. Reducing it keeps every light saying what it was saying — a blinking shot still looks different from a lit one — and takes the hard edges off. Following your device turns it on when you have asked for reduced motion.',
  'settings.flashing.system': 'Match my device',
  'settings.flashing.on': 'Reduce flashing',
  'settings.flashing.off': 'Full flashing',
  'settings.flashing-title.system': 'Reduce flashing when your device asks for reduced motion',
  'settings.flashing-title.on': 'Always take the hard edges off the machine’s lights',
  'settings.flashing-title.off': 'Let the machine flash as a real one does',
  'settings.height': 'Machine height in VR',
  'settings.height-hint':
    'How far below your eyeline the machine sits. Independent of its size, and it moves as you press — this one does nothing outside a headset.',
  'settings.height-title': 'Put the machine {position}',
  'settings.height.much-lower': 'Much lower',
  'settings.height.lower': 'Lower',
  'settings.height.level': 'Level',
  'settings.height.higher': 'Higher',
  'settings.height.much-higher': 'Much higher',
  'settings.onboarding': 'Getting started',
  'settings.onboarding-hint':
    'Play a ball, make a ramp, start a mode, save your score. They go away for good once you have done all four.',
  'settings.motion': 'Camera movement',
  'settings.motion-hint':
    'How much the view shakes and leans on a big shot. If your device asks for reduced motion the machine holds still whatever is chosen here.',
  'settings.motion.full': 'Full',
  'settings.motion.half': 'Half',
  'settings.motion.hint': 'A hint',
  'settings.motion.none': 'None',
  'settings.motion-title': 'Set camera movement to {amount}',
  'settings.graphics': 'Graphics',
  'settings.graphics-hint':
    'Automatic measures this device and picks — {describe}. Forcing a tier stops the machine stepping itself down when frames get late.',
  'settings.graphics.automatic': 'Automatic',
  'settings.graphics-automatic-title': 'Measure this device and choose automatically',
  'settings.graphics-tier-title': 'Always render at the {tier} tier',
  'settings.graphics-note':
    'A change takes effect the next time a machine is opened: rebuilding a tier mid-ball would throw away the scene under a ball in play.',
  'settings.nudging': 'Nudging',
  'settings.nudging-hint':
    'Flicking the playfield with a thumb always nudges the machine. Moving the phone itself is the extra, and it needs your browser’s permission.',
  'settings.nudging.both': 'Flick and phone motion',
  'settings.nudging.flick': 'Flick only',
  'settings.nudging-title.both': 'Nudge by flicking the playfield and by moving the phone',
  'settings.nudging-title.flick': 'Nudge by flicking the playfield only',
  'settings.nudging-refused':
    'Your browser refused the motion sensor. It can usually be re-allowed in the site settings for this page; flicking the playfield nudges either way.',
  'settings.nudging-note': 'Takes effect the next time a machine is opened.',
  'settings.touch': 'Touch controls',
  'settings.touch-hint-1':
    'Hold the left or right side of the band under the playfield for that flipper — both at once works. Drag',
  'settings.touch-hint-down': 'down',
  'settings.touch-hint-2':
    'from the bottom-right corner to pull the plunger; the further you drag, the harder the ball launches. Flick the playfield to nudge.',
  'settings.touch.show-again': 'Show the zones again',
  'settings.touch.will-show': 'The zones will be shown',
  'settings.touch-title': 'Show the flipper zones again on the next ball',
  'settings.touch-note':
    'The boundary glows until the first flipper press, then fades so the machine is not covered in buttons.',
  'settings.haptics': 'Vibration',
  'settings.haptics-hint':
    'A click when a flipper fires, a knock when the ball hits something hard, and a double tap when it drains. Nothing else on the phone buzzes.',
  'settings.haptics.on': 'On',
  'settings.haptics.off': 'Off',
  'settings.haptics-title.on': 'Feel the machine through the phone',
  'settings.haptics-title.off': 'Play without vibration',
  'settings.headsets': 'Headsets',
  'settings.headsets-hint':
    'NEON NOVA plays in VR and mixed reality on a Quest. Opening this page on the headset, or scanning the code, carries the same machine across.',
  'settings.headsets-link': 'Play in a headset',
  'settings.kept': 'Kept on this device',
  'settings.kept-looking': 'Looking…',
  'settings.kept-summary-one':
    '1 score and {replays} of the last {max} games, plus an unfinished game if you left one. No account, and nothing sent anywhere.',
  'settings.kept-summary-many':
    '{scores} scores and {replays} of the last {max} games, plus an unfinished game if you left one. No account, and nothing sent anywhere.',
  'settings.kept-unavailable':
    'This browser will not let the machine keep anything, so nothing is stored. Games still play normally; they are simply forgotten.',
  'settings.kept-forget': 'Forget everything',
  'settings.kept-forget-title': 'Delete the scores, replays and unfinished game kept on this device',
  'boundary.heading': 'That did not go to plan',
  'boundary.body':
    'Something in the page stopped working. Nothing you did caused it, and nothing you have played has been lost — scores on this device are stored as each game ends.',
  'boundary.what-next': 'What to do next',
  'boundary.reload': 'Reload',
  'boundary.reload-title': 'Reload the page',
  'boundary.back': 'Back to the machine',
  'boundary.back-title': 'Go back to the machine',
  'arcade.lede':
    'Every machine here is generated in code — no photographs, no samples, no models. Play one without an account; keep the score when you want it kept.',
  'notfound.play-title': 'Play NEON NOVA',

  // -------------------------------------------------------------------------
  // The account surface
  // -------------------------------------------------------------------------
  'account.no-backend':
    'This build has no backend configured, so accounts are unavailable. Everything else — the machine, your scores on this device, the settings — works exactly as it does with one.',
  'account.checking': 'Checking…',
  'account.lede':
    'An account puts your scores on a leaderboard, lets you add friends, and keeps your games when you change device.',
  'account.no-need': 'You do not need one to play',
  'account.what-do': 'What would you like to do',
  'account.mode.sign-in': 'Sign in',
  'account.mode.register': 'Create an account',
  'account.mode.reset': 'Send a reset link',
  'account.email-title': 'Your email address',
  'account.password-title': 'Your password',
  'account.sign-out': 'Sign out',
  'account.sign-out-title': 'Sign out of this account',
  'account.new-password-heading': 'Choose a new password',
  'account.new-password': 'New password',
  'account.new-password-title': 'Your new password',
  'account.set-password-title': 'Set this password',
  'account.handle-lede':
    'One more thing: a handle. It appears next to your scores, so it is public and it is yours — 3 to 24 characters, lower case, digits and hyphens.',
  'account.handle-title': 'Your public handle',
  'account.display-name': 'Display name (optional)',
  'account.display-name-title': 'The name shown beside your handle',
  'account.claim-title': 'Claim this handle',
  'delete.heading': 'Delete this account',
  'delete.lede':
    'Removes your profile and everything attached to it from HCC Pinball Arcade. Your sign-in itself is not deleted — the same email still works for the other applications it is used for.',
  'delete.open': 'Delete account…',
  'delete.open-title': 'See what deleting this account would remove',
  'delete.has-subscription': 'You have an active subscription.',
  'delete.not-cancelled': 'Deleting your profile does not cancel it.',
  'delete.removes': 'This removes, permanently:',
  'delete.keeps': 'This does not touch:',
  'delete.confirm-title': 'Your handle, typed exactly, to confirm the deletion',
  'delete.keep': 'Keep my account',
  'delete.keep-title': 'Keep this account',
  'delete.confirm-button-title': 'Delete this account and everything listed above',
  'friends.add': 'Add by handle',
  'friends.add-title': 'The handle of the player to add',
  'friends.send-title': 'Send a friend request',
  'friends.waiting-you': 'Waiting for you',
  'friends.waiting-them': 'Waiting for them',
  'friends.accept': 'Accept',
  'friends.decline': 'Decline',
  'friends.block': 'Block',
  'friends.empty':
    'Nobody yet. Add somebody by their handle and their scores appear on your board.',
  'ghost.needs-account': 'Replays need an account.',
  'ghost.loading': 'Loading…',
  'ghost.back': 'Back to the boards',
  'ghost.playback': 'Replay playback',
  'ghost.scrub-title': 'Scrub through the replay',
  'ghost.speed': 'Playback speed',
  'ghost.pause': 'Pause',
  'ghost.pause-title': 'Pause playback',
  'ghost.camera': 'Camera',
  'boards.heading': 'Leaderboards',
  'boards.no-backend':
    'This build has no backend configured, so there are no boards to show.',
  'boards.sign-in': 'Sign in or make one',
  'boards.input': 'Input',
  'boards.input.any': 'Any input',
  'boards.input.keyboard': 'Keyboard',
  'boards.input.touch': 'Touch',
  'boards.input.gamepad': 'Gamepad',
  'boards.input.motion': 'Motion',
  'boards.input.xr-hands': 'Hands in VR',
  'boards.input.xr-controllers': 'Controllers in VR',
  'boards.input.cabinet': 'Cabinet',
  'boards.machine-picker': 'Which machine',
  'boards.machine-title': 'Show the board for {title}',
  'boards.filters': 'Showing {period}, {scope}, {input}.',
  'boards.period': 'Period',
  'boards.scope': 'Who is on this board',
  'boards.more-title': 'Show more of this board',
  // -------------------------------------------------------------------------
  // The machines this account has made (M10-26)
  // -------------------------------------------------------------------------
  'machines.mine.heading': 'Your machines',
  'machines.mine.looking': 'Looking for your machines…',
  // A failure, said as one. "You have none" would be a sentence that makes somebody build the same
  // machine a second time.
  'machines.mine.problem': 'Your machines could not be read just now. Nothing has been lost.',
  'machines.mine.none': 'You have not made a machine yet. Designing and test-playing are free.',
  'machines.mine.open': 'Open',
  'machines.mine.open-title': 'Open {title} in the Machine Builder',
  'machines.mine.no-version': 'Nothing saved yet',
  'machines.mine.edited': 'last saved {when}',
  'machines.mine.status.draft': 'Draft',
  'machines.mine.status.review': 'In review',
  'machines.mine.status.published': 'Published',
  'machines.mine.status.unpublished': 'Unpublished',
  'headsets.heading': 'Linked Quest devices',
  'headsets.manage': 'Manage Quest devices',
  'headsets.link': 'Link another Quest',
  'headsets.refresh': 'Refresh devices',
  'headsets.multiple':
    'You can link more than one Quest to this account. Enter the separate code from each headset. Your existing devices stay linked.',
  'headsets.empty': 'No Quest devices linked yet. Choose Link another Quest and enter the code shown in the game on your headset.',
  'headsets.reference': 'Link ID {reference}',
  'headsets.looking': 'Looking for paired headsets…',
  'headsets.keep': 'Keep it',
  'headsets.remove': 'Unlink',
  'migration.empty':
    'Nothing on this browser to bring across yet. Play a game and it will be offered here.',
  'migration.heading': 'Games on this browser',
  'migration.send-title': 'Send these games to be checked',
  'pair.heading': 'Pair a headset',
  'pair.no-backend':
    'This build has no backend configured, so there is nothing for a headset to pair with.',
  'pair.go-account': 'Go to your account',
  'pair.put-on': 'Put the headset on, choose',
  'pair.code': 'Pairing code',
  'pair.code-title': 'The eight characters shown on the headset',
  'pair.lookup-title': 'Look up this pairing code',
  'pair.is-yours': 'Is this your headset?',
  'pair.meta-account': 'Meta account it says it is',
  'pair.meta-id': 'Meta ID ending',
  'pair.confirm-title': 'Confirm you started this pairing on a headset yourself',
  'pair.approve-off': 'Approve stays off until you have confirmed the line above.',
  'pair.approve-title': 'Sign this headset in as you',
  'pair.refuse-title': 'Refuse this pairing',
  'pair.see-it-on': 'Once the headset finishes signing in, you can see it and unlink it on',
  'pair.again': 'Link another Quest',
  'pair.again-title': 'Enter the code from another Quest',
  'pair.refused':
    'Refused. That headset was told no and the code is now dead — nothing can be done with it.',
  'pair.refused-safe':
    'If that was not a headset of yours, nobody got in: a refused code cannot be exchanged, and it was never a session in the first place.',

  // -------------------------------------------------------------------------
  // Friend request actions
  // -------------------------------------------------------------------------
  'friends.accept-title': 'Accept the request from {handle}',
  'friends.decline-title': 'Decline the request from {handle}',
  'friends.block-title': 'Block {handle}',
  'friends.this-player': 'this player',

  // -------------------------------------------------------------------------
  // States and fallbacks the first pass walked past
  // -------------------------------------------------------------------------
  'common.working': 'Working…',
  'common.unknown-handle': 'unknown',
  'friends.heading': 'Friends',
  'friends.sending': 'Sending…',
  'friends.send': 'Send a request',
  'account.bad-email': 'That does not look like an email address.',
  'account.set-password': 'Set this password',
  'account.claiming': 'Claiming…',
  'account.claim': 'Claim this handle',
  'delete.deleting': 'Deleting…',
  'delete.confirm': 'Delete permanently',
  'ghost.outside-db':
    'This replay is stored outside the database and cannot be watched yet.',
  'ghost.machine-gone': 'The machine this game was played on is no longer available.',
  'boards.period.all-time': 'All time',
  'boards.period.month': 'This month',
  'boards.period.week': 'This week',
  'boards.scope.global': 'Everyone',
  'boards.scope.friends': 'Friends',
  'boards.empty-friends': 'Nobody you know has a score here yet.',
  'boards.empty-global': 'No scores yet. The first one could be yours.',
  'boards.machine': 'Machine',
  'boards.loading': 'Loading…',
  'boards.more': 'Show more',
  'headsets.removing': 'Unlinking…',
  'headsets.remove-confirm': 'Yes, unlink it',
  'migration.none-sent': 'None of those games could be sent.',
  'migration.one-game': 'One game',
  'migration.one-refused': 'One game could not be accepted.',
  'migration.one-queued': 'One is still queued and will be sent when the connection allows.',
  'pair.checking-signed-in': 'Checking whether you are signed in…',
  'pair.checking': 'Checking…',
  'pair.continue': 'Continue',
  'pair.approving': 'Approving…',
  'pair.approve': 'Approve',
  'pair.refusing': 'Refusing…',
  'pair.type-again': 'Type a pairing code again',
  'pair.try-another': 'Try another code',
  'pair.start-again': 'Start again',

  // -------------------------------------------------------------------------
  // Account field labels
  // -------------------------------------------------------------------------
  'account.heading': 'Account',
  'account.email': 'Email',
  'account.password': 'Password',
  'account.handle': 'Handle',
  'account.name': 'Name',
  'account.plan': 'Plan',
  'account.plan.free': 'Free',
  'account.plan.pro-forever': 'Pro — never expires',
  'account.plan.pro-until': 'Pro — until {date}',
  'account.change': 'Change',
  'account.change-handle-title': 'Change your public handle',
  'account.change-name-title': 'Change the name shown beside your handle',
  'account.new-handle-title': 'Your new handle',
  'account.new-name-title': 'Your new display name',
  'account.save': 'Save',
  'account.save-handle-title': 'Save the new handle',
  'account.save-name-title': 'Save the new name',
  'account.saving': 'Saving…',
  'account.cancel': 'Cancel',
  'account.cancel-title': 'Keep it as it is',
  'account.saved': 'Saved.',
  'account.handle-rule':
    'Your handle is how other players find you. You can change it once every 30 days, and your old handle is kept for you for 30 days.',
  'account.handle-next': 'You can change your handle again on {date}.',

  // -------------------------------------------------------------------------
  // Account lede tail
  // -------------------------------------------------------------------------
  'account.lede-tail': 'the machine works exactly the same either way.',

  // -------------------------------------------------------------------------
  // Headset removal and game migration
  // -------------------------------------------------------------------------
  'headsets.remove-question': 'Unlink {label}?',
  'headsets.remove-title': 'Unlink {label} from your account',
  'headsets.keep-title': 'Keep {label} paired',
  'migration.sending': 'Sending {done} of {total}…',
  'migration.sent':
    '{count} sent for checking. A score appears on the board once the server has re-run it.',
  'migration.n-games': '{count} games',
  'migration.lede-tail':
    'played before you signed in. Sending them puts them in the queue to be checked — the server re-runs every one and only then does it count, exactly as it would for a game played now.',
  'migration.n-refused': '{count} games could not be accepted.',
  'migration.n-queued':
    '{count} are still queued and will be sent when the connection allows.',

  // -------------------------------------------------------------------------
  // Account deletion consequences
  // -------------------------------------------------------------------------
  'delete.consequence.0':
    'Your handle, display name and avatar. The handle becomes available to somebody else.',
  'delete.consequence.1':
    'Every score you have set, on every leaderboard, including tournament entries.',
  'delete.consequence.2':
    'Every replay you have submitted, including any that are the proof behind a board entry.',
  'delete.consequence.3': 'Every machine you have built — drafts and published versions alike.',
  'delete.consequence.4':
    'The scores other players set on machines you published, because the machines go with you.',
  'delete.consequence.5': 'Your friends list, on both sides of it.',
  'delete.consequence.6': 'Any headset paired to this account.',
  'delete.consequence.7':
    'Your subscription record here. **This does not cancel the subscription itself.**',
  'delete.keeps.0':
    'Your sign-in itself. The same email still signs in to the other applications on this account.',
  'delete.keeps.1':
    'Your Stripe or Meta subscription. **Cancel it first, or you will keep being charged.**',
  'delete.paying-tail':
    'Cancel it first, or you will keep being charged for an account that no longer exists.',
  'delete.type-handle': 'Type your handle — {handle} — to confirm',

  // -------------------------------------------------------------------------
  // Leaderboard table
  // -------------------------------------------------------------------------
  'boards.needs-account':
    'Boards need an account, because a score on one has a name next to it.',
  'boards.keep-playing': '— you can keep playing either way.',
  'boards.caption': '{machine} leaderboard, {period}, {scope}',
  'boards.rank': 'Rank',
  'boards.player': 'Player',
  'boards.score': 'Score',
  'boards.watch': 'Watch',

  // -------------------------------------------------------------------------
  // Replay viewer
  // -------------------------------------------------------------------------
  'ghost.heading': 'Watch',
  'ghost.signin': 'Sign in',
  'ghost.keep-playing': '— you can keep playing either way.',
  'ghost.checked': '{score} — checked by the server',
  'ghost.unchecked': '{score} — {status}',
  'ghost.position': 'Position {at} of {total}',
  'ghost.speed-title': 'Play at {speed} times speed',
  'ghost.camera-title': 'Watch from the {preset} camera',

  // -------------------------------------------------------------------------
  // Headset pairing details
  // -------------------------------------------------------------------------
  'pair.sign-in-first':
    'Sign in first — pairing a headset signs it in as you, so it has to be you asking.',
  'pair.put-on-tail':
    'and type the eight characters it shows here. The code is good for ten minutes.',
  'pair.sign-in-word': 'Sign in',
  'pair.code-hint':
    'Letters and digits only — the code never contains O, I, L, U, zero or one, so those keys do nothing here. {entered} of 8.',
  'pair.device': 'Device',
  'pair.as-reported': '(as reported by the headset)',
  'pair.started-myself': 'I started this pairing myself, on a headset I am holding.',
  'pair.not-mine': 'It’s not mine',
  'pair.approved': 'Approved. {device} is signing in now — it can take a few seconds.',
  'pair.your-account': 'your account',

  // -------------------------------------------------------------------------
  // Replay status, as words rather than an enum
  // -------------------------------------------------------------------------
  'ghost.status.pending': 'awaiting checking',
  'ghost.status.validated': 'checked by the server',
  'ghost.status.mismatch': 'the score did not match the replay',
  'ghost.status.divergent': 'the replay did not re-run the same way',
  'ghost.status.unsupported': 'played on a version the server cannot re-run',
  'ghost.status.oversize': 'too large to check',

  // -------------------------------------------------------------------------
  // The stage, the headset handoff and the chrome
  // -------------------------------------------------------------------------
  'stage.building': 'Building the machine',
  'stage.status': 'Machine status',
  // The flat screen's overflow menu (M3-55). Three dots, not a gear: most of what is in it is an
  // action, and a gear promises the settings page.
  'stage.menu-open': 'More',
  'stage.menu-close': 'Close the menu',
  'stage.menu-open-title': 'Open machine menu',
  'stage.menu-close-title': 'Close this menu',
  'stage.menu-label': 'Machine menu',
  'stage.menu-confirm': 'Really {action}?',
  'stage.menu-confirm-title': 'Press again to {action}. It cannot be undone.',
  'stage.menu.pause': 'Pause',
  'stage.menu.resume': 'Resume',
  'stage.menu.pause-title': 'Stop the machine where it is',
  'stage.menu.resume-title': 'Start the machine again',
  'stage.menu.settings': 'Settings',
  'stage.menu.settings-title': 'Sound, appearance and comfort',
  'stage.menu.another-machine': 'Arcade',
  'stage.menu.another-machine-title': 'Browse pinball machines and choose one to play',
  'stage.menu.end-game': 'End this game',
  'stage.menu.end-game-title': 'Give up this game and forget where you were',
  'stage.invite-gone':
    'That game has already finished. You are playing on your own — ask them to send another invite.',
  'stage.aloud': 'Read the machine aloud',
  'stage.aloud-title': 'Say what the machine is doing — ball, score, mode, what is lit, what next',
  'stage.sound-on': 'Sound on',
  'stage.sound-off': 'Sound off',
  'stage.sound-off-title': 'Mute the machine',
  'stage.sound-on-title': 'Turn the machine’s sound back on',
  'stage.pause': 'Playing',
  'stage.pause-title': 'Pause the machine',
  'stage.resume-machine': 'Paused',
  'stage.resume-machine-title': 'Let the machine carry on',
  'stage.put-ball': 'Put a ball in play',
  'stage.motion-offer': 'Nudge by moving your phone',
  'stage.motion-on-title': 'Nudge the machine by moving your phone',
  'stage.motion-off-title': 'Keep nudging by flicking the playfield',
  'stage.turn-on': 'Turn it on',
  'stage.no-thanks': 'No thanks',
  'stage.motion-enabled': 'Motion nudging is on — shove the phone to nudge.',
  'stage.motion-refused':
    'Your browser did not allow motion nudging — flick the playfield to nudge.',
  'stage.dismiss': 'Dismiss',
  'stage.dismiss-title': 'Dismiss this notice',
  'stage.focus-on': 'Focus mode',
  'stage.focus-off': 'Leave focus mode',
  'stage.focus-on-title': 'Give the machine the whole screen',
  'stage.focus-off-title': 'Show the bars again',
  'stage.players': 'Players',
  'stage.players-title': 'How many players',
  'stage.play-alone': 'Play on your own',
  'stage.play-together': 'Play together',
  'stage.opening-game': 'Opening a game…',
  'stage.open-game-title': 'Open a game on this machine that others can join',
  'stage.online-game': 'Online game',
  'stage.start': 'Start',
  'stage.start-title': 'Start the game for everybody seated',
  'stage.leave': 'Leave',
  'stage.leave-title': 'Leave this game and play on your own',
  'stage.game-ended': 'Game ended. The machine is ready for a new one.',
  'stage.your-ball': 'Your ball.',
  'stage.game-over-note': 'That game is over.',
  'stage.game-called-off': 'That game was called off.',
  'stage.somebody': 'Somebody',
  'stage.somebody-else': 'somebody else',
  'stage.resume': 'Pick it up',
  'stage.resume-heading': 'Resume your game',
  'stage.resume-title': 'Carry on with the game you left',
  'stage.start-fresh': 'Start fresh',
  'stage.start-fresh-title': 'Forget it and start a new game',
  'stage.initials-title': 'Enter your initials',
  'stage.initials-help':
    'The flippers change the letter and the plunger accepts it, exactly as on the cabinet. Pick < to go back a letter.',
  'stage.previous-letter': 'Previous letter',
  'stage.accept-letter': 'Accept this letter',
  'stage.next-letter': 'Next letter',
  'stage.ok': 'OK',
  'stage.game-over': 'Game over',
  'stage.play-again-title': 'Start another game',
  'stage.best-yet': 'Your best yet on this device',
  'stage.kept-here': 'Kept on this device.',
  'stage.cannot-keep': 'This browser will not let the machine keep anything.',
  'stage.real-score':
    'That is a real score. Keeping it, watching it back and putting it on the board all need an account.',
  'stage.make-account': 'Create a free account',
  'stage.boards-coming': 'Accounts arrive with the boards',
  'stage.keep-score': 'Keep this score',
  'stage.keep-score-title':
    'Create a free account — scores, replays and the boards cost nothing',
  'stage.pro-adds':
    'Every other machine in the arcade, and the ones other players have built, are part of Pro.',
  'stage.more-machines': 'See the other machines',
  'stage.more-machines-title': 'What Pro adds to the account you already have',
  // **The subscriber's line, and the only one here that is not an offer.** There is nothing left to
  // sell somebody who has the roster, the headset and the arcade — which is why this panel showed
  // them nothing at all — and the Builder is the half of what they pay for that a player can own
  // for months without meeting. It retires the moment they have a machine of their own.
  'stage.build-line':
    'The Machine Builder comes with your subscription. A machine of your own starts from this one.',
  // **The same destination said to everybody else, and it is not the same sentence** (M10-30).
  // The line above is addressed to somebody who has already paid for the Builder; this one is
  // addressed to a visitor, so it has to answer the question that reader actually has, which is
  // "what will this cost me". "Opens for everybody" is the honest answer — the route gates nothing
  // and a draft has survived a reload since M9-32 — and it is the reason this may appear at the end
  // of a stranger's game at all. The moment it needs a price it stops being allowed here.
  'stage.build-aside': 'A machine of your own starts in the Builder, which opens for everybody.',
  'stage.pull-down': 'PULL DOWN',
  // --- which machine the front door is playing (M9-32) -------------------------------------
  // One control, and it exists only for a browser that has a draft: a stranger's front door is
  // exactly what 08 section 1 asks for and is not changed by this at all.
  'stage.playing': 'Playing',
  'stage.switch-to-draft': 'Play your machine',
  'stage.switch-to-draft-title': 'Play the machine you are building, full screen and unscored',
  // Said beside the name when the draft is what is on screen, because a draft opened from the
  // flagship keeps the flagship's title until its author changes it — so the name alone cannot say
  // which of the two machines this is.
  'stage.your-draft': 'your machine',
  'stage.switch-to-flagship': 'Play NEON NOVA',
  'stage.switch-to-flagship-title': 'Go back to the default machine. Your machine is kept',
  // Said where the score would be, because it is a fact about the score.
  'stage.unscored': 'A draft — nothing here is scored',
  'stage.unscored-title':
    'A machine can only be ranked once it is published, because a board re-simulates the exact bytes a game was played on',
  'xr.exit': 'Exit XR',
  'xr.exit-title': 'Leave the headset and go back to the windowed machine',
  'xr.enter-vr': 'Enter VR',
  'xr.enter-mr': 'Enter MR',
  'xr.build-vr': 'Build in VR',
  'xr.build-mr': 'Build in MR',
  'xr.build-title': 'Your machine on the bench, hands only — what you came here for',
  'xr.vr-title': 'Play in the VR arcade',
  'xr.mr-title': 'Stand the machine in your own room',
  'xr.handoff-title': 'Show a code to open this on your headset',
  'xr.no-session':
    'The headset would not start a session. Try again, or check its permissions.',
  'headset.lede':
    'VR and mixed reality run in the headset’s own browser. Open this page there and the machine picks up exactly where you left it.',
  'headset.scan': 'Scan with the headset’s camera, or type the address below.',
  'headset.qr-title': 'QR code for {url}',
  'headset.step-1': 'Put the headset on and open its browser.',
  'headset.step-2': 'Scan the code above, or type the address.',
  'headset.step-3': 'Sign in if it asks — it is the same account.',
  'headset.step-4': 'Enter VR or Enter MR is on the machine itself.',
  'headset.no-headset': 'No headset yet?',
  'headset.windowed': 'The windowed machine',
  'voice.mic-on': 'Turn your microphone on',
  'voice.mic-off': 'Turn your microphone off',
  'voice.blocked': 'Your browser is not allowing this page to use a microphone',
  'voice.chat-on': 'Turn voice chat on',
  'voice.chat-off': 'Turn voice chat off',
  'nav.back': 'Go back',
  'onboarding.title': 'Getting started',
  'onboarding.progress': '{done} of {total} done',
  'onboarding.play-ball': 'Play a ball',
  'onboarding.make-ramp': 'Make a ramp',
  'onboarding.start-mode': 'Start a mode',
  'onboarding.save-score': 'Save your score',
  'onboarding.done': 'done',
  'onboarding.next': 'next',
  'onboarding.save-cta': 'Keep your scores',
  'onboarding.save-title': 'Make an account so this machine remembers you',
  'onboarding.forget': 'Show the getting-started steps again',
  'onboarding.forget-title': 'Start the four steps over',
  'nav.home-title': 'HCC Pinball Arcade — home',
  'nav.primary': 'Primary',
  'nav.scores': 'Scores',
  'theme.toggle-title': '{description}. Tap to change.',
  'play.not-found-heading': 'No machine called “{machineId}”',
  'play.not-found-body':
    'It may have been unpublished, or the link may be mistyped. The arcade has what there is.',
  'play.go-arcade': 'Go to the arcade',
  'play.browse-title': 'Browse the machines',
  // The three refusals of G1-07, and the two states a fetch has on the way to one of them.
  'play.loading': 'Getting {machineId} ready…',
  'play.loading-title': 'The machine is being fetched',
  'play.locked-heading': '“{machineId}” is part of Pro',
  'play.locked-body':
    'Every machine but the flagship comes with a subscription. NEON NOVA stays free, and so does whatever is free this week.',
  'play.locked-cta': 'See what Pro includes',
  'play.locked-title': 'Open the subscription page',
  'play.play-flagship': 'Play NEON NOVA instead',
  'play.unreadable-heading': '“{machineId}” will not run in this build',
  'play.unreadable-body':
    'The machine was fetched and this build refused it. That is a fault in the machine or in this app, never in your link — nothing about it is your doing.',
  'play.offline-heading': 'Cannot reach the arcade',
  'play.offline-body':
    'The machine list needs a connection. NEON NOVA plays without one, and anything already downloaded still opens.',
  'play.retry': 'Try again',
  'play.retry-title': 'Ask for the machine again',

  // -------------------------------------------------------------------------
  // The scoreboard beside the glass
  // -------------------------------------------------------------------------
  'scoreboard.player': 'Player {seat}',
  'scoreboard.tilted': 'tilted',
  'scoreboard.left': 'left',
  'scoreboard.playing': 'playing',

  // -------------------------------------------------------------------------
  // Headset handoff details
  // -------------------------------------------------------------------------
  'headset.no-code': 'This address cannot be turned into a scannable code',
  'headset.no-code-tail': '. Type it in instead.',
  'headset.press': 'Press',
  'headset.or': 'or',
  'headset.on-machine': 'on the machine.',
  'headset.windowed-tail': 'plays the same game, with the same physics and the same boards.',

  // -------------------------------------------------------------------------
  // Seating, turn order and watching
  // -------------------------------------------------------------------------
  'stage.take-turns': 'Take turns with {others} others on this device',
  'stage.ball-of': 'Ball {ball} of {total}',
  'stage.is-playing': '{name} is playing.',
  'stage.watching-gapped': 'Watching {name}’s ball — catching up.',
  'stage.watching-live': 'Watching {name}’s ball, live.',
  'stage.watching-diverged':
    'Watching {name}’s ball — this view is out of sync with the live game.',
  'stage.their-ball': 'It is {name}’s ball',

  // -------------------------------------------------------------------------
  // Resuming, initials and the end of a game
  // -------------------------------------------------------------------------
  'stage.left-game': 'You left a game on {score}.',
  'stage.left-game-ball': 'You left a game on ball {ball}, on {score}.',
  'stage.makes-table': '{score} makes the table. Put your initials on it.',
  'stage.skip-initials': 'Skip',
  'stage.skip-initials-title': 'Take the score without putting a name on it',
  'stage.play-again': '▸ PLAY AGAIN',
  'stage.until-then': '— until then this machine asks for nothing.',

  // -------------------------------------------------------------------------
  // The Builder workspace
  // -------------------------------------------------------------------------
  'builder.tab.layout': 'Layout',
  'builder.tab.rules': 'Rules',
  'builder.tab.modes': 'Modes',
  'builder.tab.art': 'Art',
  'builder.tab.display': 'Display',
  'builder.tab.sound': 'Sound',
  'builder.tab.speech': 'Speech',
  'builder.tab.checks': 'Checks',
  'builder.opening': 'Opening {machineId}…',
  'builder.locked-heading': '“{machineId}” is part of Pro',
  'builder.locked-body':
    'The Builder itself is open to everybody — design, test-play, take as long as you like. What a subscription buys is a machine surviving the reload, and reading somebody else\u2019s published machine.',
  'builder.not-found-body':
    'Nothing is published under that name. It may have been unpublished, or the link may be mistyped.',
  'builder.offline-body':
    'Opening a published machine needs a connection. A blank workspace does not, and it opens on the flagship body.',
  'builder.open-blank': 'Start from the flagship body',
  'builder.open-blank-title': 'Open the Builder on NEON NOVA',
  'builder.workspaces': 'Workspaces',
  'builder.tab.layout-title': 'The playfield: where everything is',
  'builder.tab.rules-title': 'The rules: what the machine does when something happens',
  'builder.tab.modes-title': 'The modes: the sets of shots a player is chasing',
  'builder.tab.art-title': 'The art: what the machine looks like, drawn in code',
  'builder.tab.display-title': 'The display: what the dot matrix shows, and when',
  'builder.tab.sound-title': 'The sound: which recipe each cue plays, and how it is tuned',
  'builder.tab.speech-title': 'The callouts: what the machine says, and the caption a player reads',
  'builder.tab.checks-title':
    'The checks: all sixteen validation classes, on the machine as it stands',
  'builder.editing': 'Editing',
  'builder.canvas-controls': 'Canvas controls',
  'builder.fit': 'Fit',
  'builder.fit-title': 'Show the whole playfield',
  'builder.true-scale': 'True scale',
  'builder.true-scale-title':
    'Show the playfield at its real size, as far as this display reports its own density',
  'builder.symmetry': 'Symmetry',
  'builder.symmetry-on': 'Snap to the centre line and to mirrored devices',
  'builder.symmetry-off': 'Stop snapping to the centre line',
  'builder.reachability': 'Reachability',
  'builder.reachability-on': 'Show where the ball can and cannot go',
  'builder.reachability-off': 'Hide the reachability shading for a moment',
  'builder.undo': 'Undo',
  'builder.undo-title': 'Undo the last change',
  'builder.redo': 'Redo',
  'builder.open-file': 'Open a file',
  'builder.open-file-title': 'Open a machine JSON file, for example one written by the MCP server',
  'builder.redo-title': 'Redo the change that was undone',
  'builder.test-play': 'Test play',
  'builder.test-play-title': 'Put a ball on this machine, here, at the real 1000 Hz (Space)',
  'builder.new-machine': 'Start a new machine',
  'builder.discard-title': 'Click again to throw away this machine and start from the template',
  'builder.discard-confirm': 'Discard and start?',
  // Throwing the kept draft away, which is the destructive half of "back to the default machine"
  // (M9-32). The other half is the switch on the front door, and it keeps everything.
  'builder.reset-default': 'Reset to the default machine',
  'builder.reset-default-title':
    'Throw this machine away and go back to NEON NOVA. The front door goes back to it too',
  'builder.reset-confirm': 'Throw this machine away?',
  'builder.kept': 'Kept in this browser. The front door can play it.',
  'builder.add-device': 'Add a device',
  // The palette's six groups (M13-12), the headset tray's tiles in the same order.
  'builder.category-flippers': 'Flippers',
  'builder.category-bumpers': 'Bumpers & slings',
  'builder.category-targets': 'Targets',
  'builder.category-lanes': 'Lanes & gates',
  'builder.category-ball': 'Ball handling',
  'builder.category-fields': 'Fields',
  'builder.remove': 'Remove',
  'builder.remove-none': 'Select a device to remove it',
  'builder.devices': 'Devices on the playfield',
  'builder.select-device': 'Select {id} ({kind})',
  'builder.select-shape': 'Shape {id} ({kind})',
  'builder.nothing-selected': 'Nothing selected. Click a device on the canvas or in the list.',
  'builder.positioned-by-targets': 'Positioned by its targets.',
  'builder.nothing-wrong': 'Nothing wrong with it',
  'builder.modes-lede':
    'A mode is a set of shots and what happens as they are made. Pick the shots on the map.',
  'builder.display-lede':
    'A scene is a list of generator operations drawn onto the dot grid every step, so it moves. The preview plays it at the rate a player sees it, with the game’s own display player.',
  'builder.speech-lede':
    'A callout is authored as sounds rather than as text, so the machine says exactly what it was told to. The caption beside it is what a player reads, and it is required.',
  'builder.rules-lede':
    'Every reference is a picker over what this machine declares, so a rule cannot name something that is not there.',

  // -------------------------------------------------------------------------
  // The display editor
  // -------------------------------------------------------------------------
  'display.none':
    'This machine declares no display, so there is nothing to compose. Start from a template.',
  'display.playback': 'Playback',
  'display.scrub': 'Scrub through the scene',
  'display.play': 'Play',
  'display.pause': 'Pause',
  'display.play-title': 'Play the scene at its real rate',
  'display.pause-title': 'Hold the scene where it is',
  'display.palette': 'Palette',
  'display.palette-title': 'The one colour the whole display glows in',
  'display.scenes': 'Scenes',
  'display.add-scene': 'Add scene',
  'display.add-scene-title': 'Add a scene with one line of text on it',
  'display.remove-scene': 'Remove scene',
  'display.remove-scene-title': 'Remove this scene, if nothing shows it',
  'display.this-scene': 'This scene',
  'display.loops': 'Loops',
  'display.loops-title':
    'Runs until something else is shown, rather than handing back to the score',
  'display.loops-hint':
    'A looping scene runs until something else is shown. A timed one hands back to the score display when it runs out.',
  'display.runs-for': 'Runs for',
  'display.runs-for-title': 'How long before the display hands back to the score',
  'display.layout': 'Layout',
  'display.layout-title': 'How many players the score display is arranged for',
  'display.score-scene':
    'A score scene has no frames: it is drawn from the game’s own scores and ball number.',
  'display.score-scene-title': 'A score scene, drawn from the game rather than from frames',
  'display.frames': 'Frames',
  'display.frames-hint':
    'Drawn in order onto a cleared display, every step. The last one is on top.',
  'display.frame-of': 'Frame {index} of {total}',
  'display.later-title': 'Draw this one later, so it goes over more',
  'display.earlier-title': 'Draw this one earlier, so more goes over it',
  'display.copy': 'Copy',
  'display.copy-title': 'Make a copy of this frame just after it',
  'display.remove': 'Remove',
  'display.remove-frame-title': 'Remove this frame',
  'display.add-frame': 'Add a frame',
  'display.add-op-title': 'Add a {op} on top',
  'display.pick-frame': 'Pick a frame to change what it draws.',
  'display.canvas-title': 'The {scene} scene, {wide} by {tall} dots',
  'display.absent-hint': '{absent} — let the renderer place it',
  'display.column-hint': '{label}: which column, counted from the left',
  'display.row-hint': '{label}: which row, counted from the top',
  'display.pair-hint': '{label} — {hint}',
  'display.invert-hint': 'Turns every dot inside out. There is nothing to set on it.',

  // -------------------------------------------------------------------------
  // The art editor
  // -------------------------------------------------------------------------
  'art.none':
    'This machine has no art block yet, so there is nothing to draw on. Start from a template.',
  'art.surfaces': 'Surfaces',
  'art.layers': 'Layers',
  'art.layers-hint':
    'Painted bottom to top: the last one is drawn over everything before it.',
  'art.layer-of': 'Layer {index} of {total}',
  'art.up-title': 'Move this layer up, so it paints over more',
  'art.down-title': 'Move this layer down, so more paints over it',
  'art.copy': 'Copy',
  'art.copy-title': 'Make a copy of this layer just above it',
  'art.remove': 'Remove',
  'art.remove-title': 'Remove this layer',
  'art.add-layer': 'Add a layer',
  'art.add-op-title': 'Add a {op} on top of the stack',
  'art.seed': 'Machine seed',
  'art.seed-title': 'The number every random layer draws from',
  'art.seed-hint':
    'One number for the whole machine. Every starfield and every streak field draws from it, in program order — so re-rolling it reshuffles all of them at once, and adding a layer below a starfield changes what that starfield draws. That is the format’s rule, not this panel’s.',
  'art.params': 'Layer parameters',
  'art.pick-layer': 'Pick a layer to change what it draws.',
  'art.surface-title': '{label} — {count} layers',
  'art.surface-title-one': '{label} — 1 layer',
  'art.layer-count': '{count} layers',
  'art.layer-count-one': '1 layer',
  'art.across-hint': '{label}: across the surface, 0 at the left and 1 at the right',
  'art.up-hint': '{label}: up the surface, 0 at the bottom and 1 at the top',
  'art.pair-hint': '{label} — {hint}',
  'art.corner-hint': '{corner} edge of the region',
  'art.first-letter': 'The colour of the first letter',
  'art.last-letter': 'The colour of the last letter',
  'art.height-hint': 'Height runs down the picture, 0 at the top.',
  'art.corner.left': 'Left',
  'art.corner.near': 'Near',
  'art.corner.right': 'Right',
  'art.corner.far': 'Far',

  // -------------------------------------------------------------------------
  // The audio editor
  // -------------------------------------------------------------------------
  'audio.cues': 'Cues',
  'audio.add-cue': 'Add cue',
  'audio.add-cue-title': 'Add a cue a rule can play',
  'audio.remove-cue': 'Remove cue',
  'audio.remove-cue-title': 'Remove this cue, if no rule plays it',
  'audio.voice': 'The voice',
  'audio.voice-hint':
    'The formant voice every callout is spoken in. The words themselves are the phoneme editor’s business.',
  'audio.voice-pitch': 'Voice pitch',
  'audio.voice-pitch-title': 'How high the callout voice speaks',
  'audio.none': 'This machine declares no cues. Add one, and a rule can play it.',
  'audio.play': 'Play',
  'audio.play-title': 'Hear this cue exactly as the game would play it',
  'audio.silent': 'There is nothing to play: this cue makes no sound',
  'audio.synth': 'Synth',
  'audio.synth-title': 'Which recipe from 07',
  'audio.notes': 'Notes',
  'audio.add-note': 'Add note',
  'audio.add-note-title': 'Strike one more bar with this cue',
  'audio.remove-note': 'Remove note',
  'audio.remove-note-title': 'Strike one fewer',
  'audio.note-title': 'Note {index}, in hertz',
  'audio.notes-hint':
    'In hertz. The bank holds three bars — 660, 880 and 1320 — and a note between them is played by transposing the nearest one, which is what a real chime bank is: a bar is inharmonic partials in fixed ratio, so transposing it is the same bar cut to a different length. With no notes at all the cue strikes the middle bar.',
  'audio.let-engine': 'Let the engine decide',
  'audio.no-rule': 'No rule plays this cue yet.',

  // -------------------------------------------------------------------------
  // Art surface labels
  // -------------------------------------------------------------------------
  'art.surface-label': '{label} — {layers}, {wide} × {tall}',
  'art.canvas-label': '{layers}, drawn at {wide} by {tall}',

  // -------------------------------------------------------------------------
  // Audio synth title
  // -------------------------------------------------------------------------
  'audio.synth-full-title': 'Which recipe from 07’s catalogue makes this sound',

  // -------------------------------------------------------------------------
  // The rules editor
  // -------------------------------------------------------------------------
  'rules.list': 'Rules',
  'rules.add': 'Add a rule',
  'rules.add-title': 'Add a rule that runs when a ball starts',
  'rules.open-title': 'Open {id} — {on}',
  'rules.pick': 'Choose a rule to see what it does.',
  'rules.remove': 'Remove this rule',
  'rules.remove-title': 'Take {id} out of the machine',
  'rules.json-title': 'The JSON of {id}',
  'rules.show-graph': 'Show the graph',
  'rules.hide-graph': 'Hide the graph',
  'rules.show-graph-title': 'Show how these rules chain, and any loop among them',
  'rules.hide-graph-title': 'Hide the graph of how these rules chain',
  'rules.sheet': 'Rulesheet',
  'rules.read-sheet': 'Read the rulesheet',
  'rules.hide-sheet': 'Hide the rulesheet',
  'rules.read-sheet-title':
    'Read this machine the way a player will — generated from the rules themselves',
  'rules.show-json': 'Show JSON',
  'rules.hide-json': 'Hide JSON',
  'rules.show-json-title': 'Show the JSON these blocks became',
  'rules.hide-json-title': 'Hide the JSON these blocks became',
  'rules.deep':
    'This condition is deeper than the blocks draw, so it is kept exactly as it is. The JSON below is the truth about it.',
  'rules.trigger-kind': 'What kind of thing sets this rule off',
  'rules.which-ball': 'Which ball event',
  'rules.which-edge': 'Which edge of it',
  'rules.all-or-any': 'Whether every row has to hold, or any one of them',
  'rules.comparison': 'How the two sides are compared',
  'rules.remove-condition': 'Take this condition out',
  'rules.add-condition': 'Add a condition this rule has to satisfy',
  'rules.side-kind': 'Whether this side is a variable or a number',
  'rules.number': 'The number to compare against',

  // -------------------------------------------------------------------------
  // The speech editor
  // -------------------------------------------------------------------------
  'speech.callouts': 'Callouts',
  'speech.add': 'Add callout',
  'speech.add-title': 'Add a callout',
  'speech.remove': 'Remove callout',
  'speech.remove-title': 'Remove this callout, if no rule speaks it',
  'speech.none': 'This machine has no callouts. Add one, and a rule can speak it.',
  'speech.say-it': 'Say it',
  'speech.say-it-title': 'Hear this callout in the machine’s own voice',
  'speech.nothing-to-say': 'There are no phonemes yet, so there is nothing to say',
  'speech.caption': 'Caption',
  'speech.caption-title': 'What a player reads on screen while this is spoken',
  'speech.caption-hint':
    'Required. Never spoken — it is what a player reads, and the words below are what the machine says.',
  'speech.sequence': 'Sequence',
  'speech.sequence-empty': 'Nothing yet. Pick a sound below.',
  'speech.remove-sound': 'Remove {name}',
  'speech.clear': 'Clear',
  'speech.clear-title': 'Take every sound out and start the line again',
  'speech.emphasis': 'Emphasis',
  'speech.stress-on': 'Stress this syllable',
  'speech.stress-off': 'Take the stress off',
  'speech.emphasis-hint':
    'A stressed syllable is held longer and lifted in pitch. A mark follows the syllable it is on, so removing a sound elsewhere in the line leaves it where you put it; removing that syllable’s own vowel drops the mark rather than moving it onto a neighbour.',
  'speech.phoneme-title': '{name} as in {example} — {ms} ms',
  'speech.arpabet':
    'ARPAbet, the alphabet 07 §4 names. The capitals in each example are the letters that make the sound.',
  'speech.no-rule': 'No rule speaks this callout yet.',

  // -------------------------------------------------------------------------
  // The publish panel
  // -------------------------------------------------------------------------
  'publish.heading': 'Save and publish',
  'publish.sign-in':
    'Sign in to save this machine. You can keep building without one — nothing here is lost by waiting.',
  'publish.drafts-note':
    'Drafts are yours and stay private. Publishing puts a version in the arcade, frozen forever, because scores are bound to it.',
  'publish.save-draft': 'Save draft',
  'publish.save-draft-title': 'Save {semver} as a private draft',
  'publish.publish': 'Publish',
  'publish.publish-pro': 'Publish — Pro',
  'publish.publish-title': 'Publish {semver} to the arcade',
  'publish.publish-pro-title': 'Publishing to the arcade is part of Pro',
  'publish.headset': 'Open in headset',
  'publish.headset-pro': 'Open in headset — Pro',
  'publish.headset-title': 'Save {semver} and get a code to open it in a Quest',
  'publish.headset-pro-title': 'Opening a draft in a headset is part of Pro',
  'publish.headset-here': 'Open in this headset',
  'publish.headset-here-title': 'Play your machine on the front door and enter MR or VR from there',
  'publish.opening-here': 'Keeping your machine, then over to the front door…',
  'publish.draft-not-kept':
    'Your machine could not be kept in this browser, so there is nothing for the headset to open. Try again.',
  'publish.saving': 'Saving…',
  'publish.saved': 'Saved {semver} as a draft.',
  'publish.saving-code': 'Saving, then making a code…',
  'publish.publishing': 'Publishing…',
  'publish.published': 'Published {semver}. It is in the arcade now.',
  'publish.review':
    'Sent for review. A person reads a first publication before it reaches the arcade.',
  'publish.no-machine': 'The machine could not be created.',
  'publish.no-version': 'The draft saved, but the server did not say which version.',
  'publish.no-code': 'The server did not return a code.',
  'publish.quest-choose': 'In the Quest app, choose',
  'publish.open-draft': 'Open a draft',
  'publish.ten-minutes': 'It lasts about ten minutes.',
  'publish.stops-at': 'It stops working at {time}.',

  // -------------------------------------------------------------------------
  // Test play
  // -------------------------------------------------------------------------
  'test.tools': 'Test-play tools',
  'test.transport': 'Freeze and step',
  'test.tools-open': 'Tools',
  'test.tools-open-title':
    'Freeze, step, slow motion, the ball map and the harnesses, over the machine',
  'test.tools-close-title': 'Put the tools away and give the machine the screen again',
  'test.stop': 'Stop',
  'test.stop-title': 'Stop the machine and go back to editing (Escape)',
  'test.step': 'Step',
  'test.step-title':
    'Advance one simulation step, which is one millisecond of this machine',
  'test.freeze': 'Freeze',
  'test.run': 'Run',
  'test.freeze-title': 'Stop the simulation where it is',
  'test.run-title': 'Let the machine run again',
  'test.speed': 'Speed',
  'test.speed-title': 'How fast time runs. The simulation rate is unchanged.',
  'test.slow-motion': 'Slow motion',
  'test.speed-hint': 'The speed control changes how fast time runs in front of you and',
  'test.place-ball': 'Click to put the ball here, at rest',
  'test.starting': 'Starting…',

  // -------------------------------------------------------------------------
  // The modes editor
  // -------------------------------------------------------------------------
  'modes.list': 'Modes',
  'modes.add': 'Add a mode',
  'modes.open-title': 'Open {title}',
  'modes.pick': 'Choose a mode, or add one.',
  'modes.remove': 'Remove this mode',
  'modes.remove-title': 'Take {title} out of the machine',
  'modes.no-objectives': 'No objectives yet. Click a device on the map to make it one.',
  'modes.remove-shot-title': 'Take {shot} out of this mode',
  'modes.stackable-on': 'Let this mode run alongside another',
  'modes.stackable-off': 'Stop this mode running alongside another',

  // -------------------------------------------------------------------------
  // The shot harness
  // -------------------------------------------------------------------------
  'harness.heading': 'Shot harness',
  'harness.settings': 'Harness settings',
  'harness.which-flipper': 'Which flipper to fire from',
  'harness.left': 'Left flipper',
  'harness.right': 'Right flipper',
  'harness.coil': 'The coil, as a fraction of what the machine declares',
  'harness.stop': 'Stop',
  'harness.stop-title': 'Stop firing',
  'harness.not-run': 'not run',
  'harness.done': 'done',

  // -------------------------------------------------------------------------
  // The last of the Builder
  // -------------------------------------------------------------------------
  'actions.add': 'Add an action…',
  'actions.add-title': 'Add something for this to do',
  'actions.remove-title': 'Take this action out',
  'actions.number-title': 'The number this action uses',
  'actions.value-title': 'One of the values this action allows',
  'flipper.heading': 'Flipper geometry',
  'flipper.figure': 'Flipper geometry (PH-054)',
  'flipper.select': 'Select {id}',
  'flipper.no-overlap': 'no interpenetration',
  'flipper.crossing': 'the bats cross, so there is no centre drain at all',
  'force.heading': 'Force state',
  'force.none': 'This machine declares no modes, so there is none to jump to.',
  'force.start': 'Start',
  'force.end': 'End',
  'height.too-few': 'Two points make a run. This one has fewer.',
  'height.steepest': 'Steepest',
  'inspector.pick': 'Select a device to see what it can be set to.',
  'inspector.nothing':
    'Nothing to set on this one — it is placed and shaped, and that is all it has.',
  'inspector.properties': 'Properties of {id}',
  'inspector.range': '{label} — between {min} and {max}',
  'inspector.set': 'Set',
  'inspector.default': 'Default',
  'canvas.label':
    'Playfield layout. Drag a device to move it; the arrow keys nudge the selected device by a millimetre.',
  'ramp.hint':
    'Drag a point on the playfield to route the ramp; set how high it climbs here.',
  'ramp.shape-of': 'Shape of {id}',
  'ramp.select-point': 'Select point {index} of {total}',
  'ramp.height-of': 'Height of point {index}, millimetres above the playfield',
  'ramp.height-range': 'Height of point {index} in millimetres, between {min} and {max}',
  'ramp.add-point': 'Add a point halfway to the next one, without changing the shape',
  'ramp.remove': 'Remove {id}',
  'ramp.remove-title': 'Take {id} off the machine, with its switches and supports. Undo brings it back.',
  'graph.none': 'No rules yet, so nothing chains to anything.',
  'graph.unguarded':
    'Every rule in this loop fires unconditionally, so it will run until the engine cuts it off',
  'graph.mode-hooks': 'mode hooks',
  'graph.loop': 'Loop',
  'graph.guarded-loop': 'Guarded loop',
  'harness.fire': 'Fire 200',
  'shotmap.label':
    'Playfield map. {count} shots chosen. Click a device to add or remove it.',
  'soak.settings': 'Soak settings',
  'soak.how-many': 'How many balls to play',
  'soak.run': 'Soak',
  'soak.stop': 'Stop',
  'soak.stop-title': 'Stop the run',
  'soak.not-run': 'not run',
  'soak.hint':
    'Plays headless balls with a flipper-mashing robot and reports what got stuck. One ball in ten thousand parking in a pocket is one game in a few hundred that ends with the machine hunting for its ball — and you never see it, because it never happens to you.',
  'validation.all-pass': 'Every class this machine can be checked against passes.',
  'validation.run-soak': 'run the soak',

  // -------------------------------------------------------------------------
  // Graph edges and the Quest hand-off
  // -------------------------------------------------------------------------
  'graph.edge-title': '{from} → {to} ({rules})',
  'publish.quest-tail': 'and type this code. It opens',

  // -------------------------------------------------------------------------
  // The last of the chrome
  // -------------------------------------------------------------------------
  'soak.play-title': 'Play {count} balls',
  'common.loading': 'Loading…',
  'notfound.heading': 'Nothing at this address',
  'notfound.on-the-way': 'It is on the way. Until then, there is a machine waiting.',
  'test.speed-not': 'not',
  'test.speed-hint-tail':
    'the simulation: a step is a millisecond of machine at every setting, so what you watch at 0.2x is exactly what happens at full rate, five times more slowly.',

  // -------------------------------------------------------------------------
  // Validation class verdicts
  // -------------------------------------------------------------------------
  'validation.refuses': 'refuses',
  'validation.warns': 'warns',
  'validation.passes': 'passes',

  // -------------------------------------------------------------------------
  // Navigation, page titles and the theme toggle
  // -------------------------------------------------------------------------
  'nav.tab.arcade': 'Arcade',
  'nav.tab.boards': 'Boards',
  'nav.tab.play': 'Play',
  'nav.tab.build': 'Build',
  'nav.tab.you': 'You',
  'nav.tab.arcade-title': 'Browse the machines',
  'nav.tab.boards-title': 'Leaderboards',
  'nav.tab.play-title': 'Play NEON NOVA',
  'nav.tab.build-title': 'The Machine Builder',
  'nav.tab.you-title': 'Your scores and settings',
  'nav.back-label': 'Back',
  'route.title.play': 'NEON NOVA',
  'route.title.arcade': 'Arcade',
  'route.title.play-machine': 'Play',
  'route.title.machine-guide': 'Machine guide',
  'route.title.settings': 'Settings',
  'route.title.headset': 'Open on your headset',
  'route.title.boards': 'Leaderboards',
  'route.title.account': 'Account',
  'route.title.watch': 'Watch',
  'theme.system': 'Following your system — currently {appearance}',
  'theme.always': 'Always {choice}',
  'theme.appearance.light': 'light',
  'theme.appearance.dark': 'dark',

  // -------------------------------------------------------------------------
  // The remaining page titles
  // -------------------------------------------------------------------------
  'route.title.link-headset': 'Pair a headset',
  'boards.friends-link': 'Friends, presence and invites',
  'friends.profile-title': 'Open their page',
  'friends.lede': 'Ask by handle, answer what you are asked, and see who is about.',
  'friends.needs-account': 'Friends need an account — the list is on the server rather than in this browser.',
  'friends.account': 'Go to your account',
  'friends.account-title': 'Open the account page',
  'friends.online': 'in the arcade',
  'friends.invite': 'Invite to a machine',
  'friends.invite-title': 'Start a game and send them the link',
  'friends.invite-copied': 'Link copied. Send it to them — it seats them at the machine you are on.',
  'friends.invite-failed': 'Could not start a game to invite them to. Try again in a moment.',

  'history.heading': 'Your games',
  'history.lede': 'Every game this browser has finished. The ones that reached a board can be watched back.',
  'history.loading': 'Reading this browser…',
  'history.empty': 'Nothing yet. Play a ball and it will be here.',
  'history.play': 'Play',
  'history.play-title': 'Go to the machine',
  'history.caption': '{count} finished games',
  'history.machine': 'Machine',
  'history.score': 'Score',
  'history.when': 'When',
  'history.where': 'Kept',
  'history.watch': 'Watch',
  'history.watch-title': 'Watch this game back',
  'history.origin.posted': 'On a board',
  'history.origin.queued': 'Waiting to be posted',
  'history.origin.refused': 'Not accepted',
  'history.origin.local': 'This browser only',

  'profile.heading': 'Player',
  'profile.loading': 'Looking them up…',
  'profile.missing': 'No player called “{handle}”.',
  'profile.boards': 'Go to the boards',
  'profile.boards-title': 'Open the leaderboards',
  'profile.progress': 'On the board for {reached} of {total} machines.',
  'profile.best': 'Best scores',
  'profile.no-scores': 'No scores on any board yet.',
  'profile.machines': 'Published machines',
  'profile.no-machines': 'No published machines.',

  'tournament.heading': 'Tournament',
  'tournament.loading': 'Asking for this week’s…',
  'tournament.none': 'No tournament is running. The next one opens when this week’s closes.',
  'tournament.needs-account': 'A tournament needs an account — an entry is a validated game, and a game needs somewhere to be validated against.',
  'tournament.window': 'Open from {opens} until {closes}.',
  'tournament.fixed': 'Fixed seed {seed}, settings {settings}.',
  'tournament.core': 'Simulation core v{version}.',
  'tournament.core-mismatch':
    'This tournament replays on core v{version}, but this client plays on v{ours} — games played here cannot enter it.',
  'tournament.defaults':
    'Everybody plays the machine’s defaults. A game played with an operator adjustment changed ranks on its own board rather than this one.',
  'tournament.play': 'Play the machine',
  'tournament.play-title': 'Go and play a game to enter',
  'tournament.nothing-to-enter': 'Nothing to enter yet — finish a game and let it reach a board first.',
  'tournament.enter': 'Enter your {score}',
  'tournament.enter-title': 'Enter your best validated game',
  'tournament.entered': 'Entered. Your game is on the standings.',
  'tournament.standings': 'Standings',
  'tournament.empty': 'Nobody has entered yet.',

  // The moderation queue (M9-28, 11 §6). Translated like every other route, because 01 §6 says
  // *the whole web client* reads in either language and a moderator is a player with one extra
  // column set on their profile, not a member of staff who was issued an English build.
  // 11 §7's remix, on `/builder/:machineId` (M9-46).
  'remix.looking-at': 'You are looking at {title} by {author}. Remix it to keep your changes.',
  'remix.take': 'Remix it into a machine of my own',
  'remix.take-title': 'Fork this into a new machine you own, keeping who it came from',
  'remix.needs-pro': 'Keeping a machine is part of Pro. You can still edit and test-play this one.',
  'remix.needs-account': 'A remix needs an account, because the new machine belongs to somebody.',
  'remix.failed': 'That could not be remixed. Try again in a moment.',

  // 11 §6's report control, on the machine screen (M9-28).
  'stage.menu.report': 'Report this machine',
  'stage.menu.report-title': 'Tell a moderator something is wrong with it',
  'report.heading': 'Report this machine',
  'report.about': 'A moderator reads every report. This is about {title}.',
  'report.reason-legend': 'What is wrong?',
  'report.reason.offensive-text': 'Offensive text',
  'report.reason.real-machine': 'A copy of a real machine',
  'report.reason.broken': 'It does not work',
  'report.reason.misleading-rating': 'The content rating is wrong',
  'report.reason.other': 'Something else',
  'report.detail': 'Anything else a moderator should know (optional)',
  'report.detail-title': 'Up to 400 characters, shown only to moderators',
  'report.send': 'Send the report',
  'report.send-title': 'Send this to a moderator',
  'report.close': 'Close',
  'report.close-title': 'Close without sending',
  'report.sent': 'Thank you. A moderator will read it.',
  'report.needs-account': 'A report needs an account, so one person cannot look like a crowd.',
  'report.failed': 'That could not be sent. Try again in a moment.',

  'keys.heading': 'Builder API and AI tools',
  'keys.lead':
    'A key lets a tool or an AI build machines on this account. Download the MCP server, then make a key for it.',
  'keys.shown-once': 'This is the only time you will see this key. Copy it now.',
  'keys.copy': 'Copy',
  'keys.copied': 'Copied.',
  'keys.copied-title': 'Copy the key to the clipboard',
  'keys.done': 'Done',
  'keys.done-title': 'Hide the key',
  'keys.label': 'What is this key for?',
  'keys.label-title': 'A name for this key, up to 40 characters',
  'keys.label-example': 'Claude Code on the study PC',
  'keys.scopes': 'What it may do',
  'keys.scope.read': 'Read my machines',
  'keys.scope.write': 'Create and save drafts',
  'keys.scope.publish': 'Publish to the arcade',
  'keys.new': 'Make a key',
  'keys.new-pro': 'Make a key (Pro)',
  'keys.new-title': 'Mint a new Builder API key',
  'keys.looking': 'Looking for your keys…',
  'keys.none': 'No keys yet.',
  'keys.too-many': 'You already have five live keys. Revoke one to make another.',
  'keys.revoke': 'Revoke',
  'keys.revoke-confirm': 'Yes, revoke it',
  'keys.revoke-question': 'Revoke {label}? Any tool using it stops at once.',
  'keys.revoke-title': 'Revoke {label}',
  'keys.revoked': '{label} was revoked.',
  'keys.keep': 'Keep',
  'keys.keep-title': 'Keep this key',
  'package.get': 'Get the download link',
  'package.get-title': 'Ask for a link to the MCP server file; it lasts ten minutes',
  'package.save': 'Save {file}',
  'package.save-title': 'One JavaScript file; the link lasts ten minutes',
  'package.sha': 'SHA-256 {digest}',
  'package.node': 'Needs Node 22 or newer',
  'package.how': 'How to connect it to Claude, Cursor or another agent',
  'package.none': 'No package has been published yet.',
  'moderation.heading': 'Moderation queue',
  'moderation.lede':
    'Oldest first. Dismissing publishes the machine; upholding sends it back to the author as a draft they can fix.',
  'moderation.loading': 'Asking for the queue…',
  'moderation.empty': 'Nothing is waiting. Every publication has been read.',
  'moderation.needs-account': 'The queue needs an account.',
  'moderation.not-a-moderator': 'This page is for moderators.',
  'moderation.by': 'By {author}, who has published {published} before. Waiting since {opened}.',
  'moderation.author-unknown': 'an author with no handle',
  'moderation.why': 'Queued because: {reasons}. Reports: {reports}.',
  'moderation.screen-clean': 'The automated screen found nothing mechanical.',
  'moderation.reason.first-publication': 'first publication',
  'moderation.reason.screen-findings': 'the screen found something',
  'moderation.reason.reported': 'reported by a player',
  'moderation.reason.sampled': 'sampled',
  'moderation.open': 'Open it in the Builder',
  'moderation.open-title': 'Look at the machine before deciding',
  'moderation.notes': 'Notes for the next reviewer',
  'moderation.notes-title': 'Kept for reviewers, never shown to the author',
  'moderation.dismiss': 'Nothing wrong — publish it',
  'moderation.dismiss-title': 'Dismiss the concern and put the machine in the arcade',
  'moderation.uphold': 'Something is wrong — send it back',
  'moderation.uphold-title': 'Uphold the concern, warn the author, return the version to draft',
  'moderation.takedown': 'Take it down',
  'moderation.takedown-title': 'Withdraw this published version immediately; scores are kept',
  'moderation.decided': '{title} is now {status}.',
  'moderation.taken-down': '{title} has been taken down.',
  'moderation.saved-from-tool': 'saved from a tool',
  'moderation.saved-from-headset': 'saved from a headset',
  'moderation.block': 'Block author',
  'moderation.block-title': 'Stop this author building machines',
  'moderation.block-reason': 'Why this author is blocked',
  'moderation.block-reason-title': 'Kept with the block, for the next moderator; up to 200 characters',
  'moderation.block-confirm': 'Block',
  'moderation.block-keep': 'Keep',
  'moderation.block-keep-title': 'Do not block this author',
  'moderation.blocked': 'Blocked. Their keys were revoked and the Builder routes refuse them.',
  'pricing.heading': 'What it costs',
  'pricing.lede':
    'NEON NOVA is free to play with no account, for ever. Pro is everything else — the roster, the Builder, the headset, the arcade.',
  'pricing.annual': 'A year of Pro',
  'pricing.monthly': 'A month of Pro',
  'pricing.per-month': '{amount} a month, billed yearly',
  'pricing.saving': 'Save {percent}%',
  'pricing.included': 'What Pro adds',
  'pricing.free-forever':
    'A free account keeps your scores, your history and your replays, and puts you on the leaderboards. That does not expire and does not cost anything.',
  'pricing.cta': 'Get Pro',
  'pricing.cta-title': 'Go to the account page and subscribe',
  'pricing.play': 'Play for free instead',
  'route.title.pricing': 'Pricing',
  'route.title.friends': 'Friends',
  'route.title.history': 'Your games',
  'route.title.profile': 'Player',
  'route.title.tournaments': 'Tournament',
  'route.title.moderation': 'Moderation',
  'route.title.builder-machine': 'Machine Builder',
  'route.title.builder': 'Machine Builder',
  'arcade.pricing-link': 'What Pro costs',
  'paywall.pricing-link': 'See the full pricing, and what is free for ever',
  'route.title.how-to-play': 'How to play',
  'route.title.privacy': 'Privacy',
  'route.title.accessibility': 'Accessibility',
  'route.title.terms': 'Terms',
  'route.title.developers': 'Developers',
  'route.title.connect': 'Connect your AI',
  'route.title.support': 'Support',
  'route.title.about': 'About',
  'arcade.heading': 'The machines',
  'footer.heading': 'More from HCC Pinball',
  'footer.machines': 'Machines',
  'route.title.not-found': 'Not found',

  // -------------------------------------------------------------------------
  // The key guide (M3-56, 08 §4.3)
  // -------------------------------------------------------------------------
  // The keycaps are `keys.*` and only the ones that are *words* are here: `←` and `W` are the
  // same in every language and live in `keyGuide.ts` as glyphs.
  'guide.title': 'Controls',
  'guide.show-title': 'Show what the keys do',
  'guide.hide-title': 'Hide the controls',
  'guide.flipper-left': 'Left flipper',
  'guide.flipper-right': 'Right flipper',
  'guide.plunger': 'Plunger',
  'guide.plunger-note': 'Hold, then let go. The longer you hold it, the harder the ball launches.',
  'guide.nudge': 'Nudge',
  'guide.nudge-note': 'Shove the machine. Too hard and it tilts.',
  'guide.pause': 'Pause',
  'keys.shift': 'Shift',
  'keys.space': 'Space',
  'keys.enter': 'Enter',
} as const

export type MessageKey = keyof typeof EN

/** Every key, for the test that holds the bundles to each other. */
export const MESSAGE_KEYS = Object.keys(EN) as readonly MessageKey[]
